import axios from "axios";
import router from "../router";
import { authHeader } from "@/_helpers";
import { tokenService } from "./token.service";

const client = axios.create({ baseURL: process.env.VUE_APP_GATEWAY_URL });

client.interceptors.request.use(config => {
  config.headers = { ...config.headers, ...authHeader() };
  return config;
});

// Retry once after a transparent token refresh on 401
client.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await tokenService.refreshAccessToken();
        originalRequest.headers = { ...originalRequest.headers, ...authHeader() };
        return client(originalRequest);
      } catch {
        router.push({ name: "login" });
      }
    }
    return Promise.reject(error);
  }
);

function get(path, params = {}) {
  return client.get(path, { params }).then(r => r.data);
}

function post(path, body = {}) {
  return client.post(path, body).then(r => r.data);
}

export const gatewayService = {
  commodities: {
    intradayHistory: params => get("/commodities/intraday-history", params),
    technicalAnalysis: params => get("/commodities/technical-analysis", params),
    valuationInputs: params => get("/commodities/valuation-inputs", params),
    budget: params => get("/commodities/budget", params),
    program: params => get("/commodities/program", params),
    strategy: params => get("/commodities/strategy", params),
    exposureOption: params => get("/commodities/exposure-option", params),
    history: params => get("/commodities/history", params),
    prices: params => get("/commodities/prices", params),
    // Data-model writes: Django reads the payload from request.data["params"]
    updateExposure: params => post("/commodities/update-exposure", { params }),
    updateHedge: params => post("/commodities/update-hedge", { params }),
    deleteExposure: params => post("/commodities/delete-exposure", { params }),
    deleteHedge: params => post("/commodities/delete-hedge", { params })
  },
  currencies: {
    intradayHistory: params => get("/currencies/intraday-history", params),
    valuationInputs: params => get("/currencies/valuation-inputs", params),
    program: params => get("/currencies/program", params),
    ohlcFwdVolRr: params => get("/currencies/ohlc-fwd-vol-rr", params),
    strategy: params => get("/currencies/strategy", params),
    exposureOption: params => get("/currencies/exposure-option", params),
    forecastMeasures: params => get("/currencies/forecast-measures", params),
    prices: params => get("/currencies/prices", params),
    // Data-model writes: Django reads the payload from request.data["params"]
    updateExposure: params => post("/currencies/update-exposure", { params }),
    updateHedge: params => post("/currencies/update-hedge", { params }),
    deleteExposure: params => post("/currencies/delete-exposure", { params }),
    deleteHedge: params => post("/currencies/delete-hedge", { params })
  },
  market: {
    correlations: params => get("/market/correlations", params),
    technicalAnalysis: params => get("/market/technical-analysis", params),
    etfHistory: params => get("/market/etfs/history", params),
    futuresHistory: params => get("/market/futures/history", params),
    indexHistory: params => get("/market/indexes/history", params),
    interestRateHistory: params => get("/market/interest-rates/history", params),
    macroIndicators: params => get("/market/macro-indicators", params),
    fepHistory: params => get("/market/fep-history", params),
    colombiaFepExpo: params => get("/market/colombia-fep-expo", params)
  },
  catalogs: {
    optionTypes: params => get("/catalogs/option-types", params),
    pricingMethods: params => get("/catalogs/pricing-methods", params),
    incoterms: params => get("/catalogs/incoterms", params),
    menu: params => get("/catalogs/menu", params),
    menuCommodities: params => get("/catalogs/menu-commodities", params)
  },
  user: {
    dataModelInfo: params => get("/user/data-model-info", params),
    overviewChoices: params => get("/user/overview-choices", params),
    // Data-model table export: Django builds the .xlsm, then it is fetched as a blob
    createDataModelFile: params =>
      post("/user/create-data-model-file", { params }),
    downloadDataModelFile: params =>
      client
        .get("/user/download-data-model-file", { params, responseType: "blob" })
        .then(r => r.data)
  },
  get,
  post
};
